import React from 'react';
import { getImage, GatsbyImage, withArtDirection } from 'gatsby-plugin-image';
import { graphql } from 'gatsby';
import tw, { styled } from 'twin.macro';
import Bubble from '../svgs/bubble';
import InView from 'react-intersection-observer';
import Icon from '../svgs/icon';
import { motion } from 'framer-motion';

const mqs = {
  image_2: '(min-width: 640px)',
  image_3: '(min-width: 1280px)',
};

const HeroImageWrapper = styled.div`
  position: relative;
  padding-bottom: ${(6 / 13) * 100}%;

  @media ${mqs.image_2} {
    padding-bottom: ${(1 / 4) * 100}%;
  }

  @media ${mqs.image_3} {
    padding-bottom: ${(1 / 6) * 100}%;
  }
`;

const HeroImage = styled(GatsbyImage)`
  ${tw`!absolute !inset-0`}
`;

const HeroBanner = ({ data }) => {
  const images = withArtDirection(getImage(data.image.localFile), [
    {
      media: mqs.image_2,
      image: getImage(data.image_2.localFile),
    },
    {
      media: mqs.image_3,
      image: getImage(data.image_3.localFile),
    },
  ]);
  return (
    <div tw="relative z-50 mb-24 lg:mb-8 xl:mb-24">
      <HeroImageWrapper className="half-tone">
        <HeroImage image={images} alt={data.image.alternativeText} />
      </HeroImageWrapper>
      <div tw="absolute inset-0 flex items-center justify-center pt-24 xl:pt-40">
        <InView triggerOnce={true} threshold={0.7}>
          {({ inView, ref }) => (
            <motion.div
              initial="out"
              animate={inView ? 'in' : 'out'}
              transition={{ delayChildren: 0.8 }}
              ref={ref}
              tw="relative"
            >
              <Bubble
                tw="h-full w-52 text-purple-50 xl:w-72"
                useInView={false}
              />
              <motion.div
                transition={{ staggerChildren: 0.2, delayChildren: 0.6 }}
                tw="absolute flex flex-col items-center justify-center inset-0 px-6 pb-10 text-center xl:pb-16"
              >
                {data.icon && (
                  <Icon
                    name={data.icon}
                    tw="w-12 h-12 inline-block mb-2"
                    useInView={false}
                  />
                )}
                <motion.h1
                  variants={{ in: { opacity: 1 }, out: { opacity: 0 } }}
                  tw="font-bold text-brand-purple-800 text-2xl leading-none xl:(text-4xl leading-none)"
                >
                  {data.banner_title}
                </motion.h1>
              </motion.div>
            </motion.div>
          )}
        </InView>
      </div>
    </div>
  );
};

export const query = graphql`
  fragment BannerImageA on STRAPI_UploadFile {
    url
    alternativeText
    localFile {
      childImageSharp {
        gatsbyImageData(
          aspectRatio: 2.16666667
          layout: FULL_WIDTH
          transformOptions: { cropFocus: ENTROPY }
          formats: [AUTO, WEBP, AVIF]
        )
      }
    }
  }
  fragment BannerImageB on STRAPI_UploadFile {
    url
    alternativeText
    localFile {
      childImageSharp {
        gatsbyImageData(
          aspectRatio: 4
          layout: FULL_WIDTH
          transformOptions: { cropFocus: ENTROPY }
          formats: [AUTO, WEBP, AVIF]
        )
      }
    }
  }
  fragment BannerImageC on STRAPI_UploadFile {
    url
    alternativeText
    localFile {
      childImageSharp {
        gatsbyImageData(
          aspectRatio: 6
          layout: FULL_WIDTH
          transformOptions: { cropFocus: ENTROPY }
          formats: [AUTO, WEBP, AVIF]
        )
      }
    }
  }
  fragment HeroBanner on STRAPI_Indicator {
    icon
    banner_title
    image: banner_image {
      ...BannerImageA
    }
    image_2: banner_image {
      ...BannerImageB
    }
    image_3: banner_image {
      ...BannerImageC
    }
  }
  fragment PageHeroBanner on STRAPI_Page {
    icon
    banner_title
    image: banner_image {
      ...BannerImageA
    }
    image_2: banner_image {
      ...BannerImageB
    }
    image_3: banner_image {
      ...BannerImageC
    }
  }
`;
export default HeroBanner;
